/** September 12 review additions. Gate membership and evidence rules are data. */
export const initialComplianceSeeds = [
    {
        version: 2, key: 'initial-clearance-scope-review', label: 'Role, employer and clearance scope review',
        kind: 'document', owner: 'agency', phase: 'onboarding', expiry: { mode: 'none' },
        gates: ['employment', 'independent-service'], countsTowardProgress: false,
        compliance: { evidenceMode: 'scope', reviewOwner: 'HR' },
        citations: ['12VAC5-381-200', 'CHAP HRM.1–7', 'September 12 review F01/F11/F12'], fileCategory: 'employee-records',
    },
    {
        version: 2, key: 'information-truthfulness-statement', label: 'Signed information accuracy and truthfulness statement',
        kind: 'document', owner: 'person', phase: 'onboarding', expiry: { mode: 'none' }, gates: ['employment'],
        compliance: { evidenceMode: 'signed' },
        citations: ['LD 7.25.1'], fileCategory: 'employee-records',
    },
    {
        key: 'compliance-coi-orientation', label: 'Compliance and conflict-of-interest education',
        kind: 'document', owner: 'person', phase: 'onboarding', expiry: { mode: 'none' }, gates: ['employment'],
        compliance: { evidenceMode: 'education' },
        citations: ['LD 7.17.1', 'LD 7.25.1', 'CHAP CP.8'], fileCategory: 'training-competency',
    },
    {
        version: 2, key: 'annual-compliance-education', label: 'Annual compliance education',
        kind: 'recurring-event', owner: 'person', phase: 'continuous',
        expiry: { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        compliance: { evidenceMode: 'education', firstReviewMonths: 12, firstReviewRequirementKey: 'compliance-coi-orientation', reviewOwner: 'HR and Compliance' },
        citations: ['LD 7.25.1', 'CHAP CP.8'], fileCategory: 'training-competency',
    },
    {
        key: 'criminal-sworn-disclosure', label: 'Sworn criminal-history disclosure',
        kind: 'document', owner: 'person', phase: 'onboarding', expiry: { mode: 'none' }, gates: ['employment'],
        compliance: { evidenceMode: 'signed' },
        citations: ['Virginia Code 32.1-162.9:1'], fileCategory: 'employee-records',
    },
    {
        // #175 (Julio, September 17, 2026). No Vigeo P&P names this yet, so the statute and the
        // DARS form are the source. Clinical and field roles only: it sits in `set-clinical`.
        // Signed once at hire and again when DARS revises the form. That is a revision trigger,
        // not an interval — the same shape as catalog Q48 for `job-description-signed` — so the
        // expiry is none. The pdf-form pins its template inside the definition's hashed content,
        // so a DARS revision is a new definition version to sign.
        key: 'mandated-reporter-acknowledgment', label: 'Signed mandated reporter acknowledgment',
        legalName: 'Acknowledgement of Mandated Reporter Status (DARS Adult Protective Services, Rev 6/2026)',
        kind: 'document', owner: 'person', phase: 'onboarding', expiry: { mode: 'none' }, gates: ['employment'],
        compliance: { evidenceMode: 'signed', instructions: 'The evidence is page 1 of the current DARS form, signed and dated by the employee. Re-sign when DARS revises the form.' },
        citations: ['Virginia Code 63.2-1606', 'DARS APS Acknowledgement of Mandated Reporter Status (Rev 6/2026)'], fileCategory: 'employee-records',
    },
    {
        /*
         * PV1 — the agency's own Patient Visit Rules acknowledgment (#372 part 2, Paul 2026-09-29/30).
         *
         * NOT EVV, which is what an earlier reading of the ticket guessed. Paul: "It's not EVV. It's
         * our agency's Patient Visit Rules Acknowledgment (conduct in patients' homes: safety and
         * privacy)." EVV-type content is a different row entirely — the visit signature and payroll
         * verification attestation, which Paul numbers as form 22 and which arrives at orientation.
         *
         * ITS SOURCE IS THE ONBOARDING PACKET, NOT A P&P NUMBER, and that is recorded rather than
         * dressed up. Paul, asked directly because `UIUX-FIGMA-HANDOFF.md` says a form with no policy
         * basis is flagged rather than added (tracker #11):
         *
         *   "The source is our onboarding packet, Section 13, 'Patient Visit Rules' … It doesn't have
         *    a P&P number yet, so for now please treat packet Section 13 as the approved source. I'll
         *    get a policy number assigned through P&P revision."
         *
         * So the citation names the packet section. When the revision lands, the P&P number is added
         * beside it; until then nobody reading this row can mistake it for policy-backed.
         *
         * EVERY ROLE, because every employee signs the packet. Paul: "It's the same form every current
         * employee has signed", and asked whether this and the HIPAA form are both in it: "yes both
         * part of the onboarding/employee packet/application". The precedent next door points the
         * other way — `mandated-reporter-acknowledgment` is `set-clinical`, clinical and field roles
         * only — and on the content alone ("conduct in patients' homes") that would fit. It is in
         * `set-core` because what the agency actually collects decides this, not what the title
         * implies, and under-scoping a compliance row means missing signatures while over-scoping
         * means an office hire signs a form they already sign today.
         *
         * Gates `employment` and is not waivable, like the rest of the packet's signed acknowledgments
         * and like the HIPAA form Paul ruled on the same day ("No exception"). Stated here rather than
         * left to the default so it reads as a decision.
         */
        version: 2, key: 'patient-visit-rules-acknowledgment', label: 'Patient visit rules acknowledgment',
        legalName: 'Patient Visit Rules Acknowledgment (pre-onboarding packet, Section 13)',
        kind: 'document', owner: 'person', phase: 'onboarding', expiry: { mode: 'none' }, gates: ['employment'],
        waivable: false,
        compliance: {
            evidenceMode: 'signed',
            instructions: 'The evidence is the signed Patient Visit Rules page from the pre-onboarding packet (Section 13), dated by the employee. It covers conduct in patients\u2019 homes: safety and privacy.',
        },
        citations: ['Pre-onboarding packet, Section 13 (Patient Visit Rules)', 'No P&P number yet \u2014 assignment in progress through P&P revision'],
        fileCategory: 'employee-records',
    },
    ...['medical-clearance', 'fit-test', 'training'].map((part) => ({
        version: 2, key: `respirator-${part}`,
        label: { 'medical-clearance': 'Respirator medical clearance', 'fit-test': 'Respirator fit test', training: 'Respirator training' }[part],
        kind: part === 'medical-clearance' ? 'document' : 'recurring-event', owner: 'person', phase: 'both',
        expiry: part === 'medical-clearance' ? { mode: 'none' } : { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        gates: ['independent-service'],
        trigger: { mode: 'conditional', clauses: [{ kind: 'attribute', attribute: 'requires-respirator', anyOf: ['yes'] }], showBeforeTrigger: true },
        compliance: { evidenceMode: part === 'training' ? 'education' : 'reviewed' },
        citations: ['29 CFR 1910.134', 'CHAP IPC.2'],
        fileCategory: part === 'medical-clearance' ? 'segregated-health' : 'training-competency',
    })),
];
//# sourceMappingURL=compliance.seed.js.map